import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { adminApi, getErrorMessage } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Card, num } from "./ui";
import { fmtDate } from "./assess/parts";

function Days({ days }) {
  const max = Math.max(1, ...days.map((d) => d.calls));
  return (
    <div className="flex items-end gap-1.5 h-16" data-testid="wi-usage-days">
      {days.map((d) => (
        <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day}: ${d.calls} calls`}>
          <div className="w-full bg-[#73bf44]/70 rounded-sm min-h-[2px]" style={{ height: `${(d.calls / max) * 100}%` }} />
          <span className="text-[0.55rem] text-neutral-600">{d.day.slice(8)}</span>
        </div>
      ))}
    </div>
  );
}

function SyncState({ s }) {
  const r = s.last_run || {};
  return (
    <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs" data-testid="wi-usage-sync">
      <dt className="text-neutral-500">Last sync</dt><dd className="text-neutral-200">{s.last_sync_at ? `${fmtDate(s.last_sync_at)} · ${r.mode}` : "Not run yet"}</dd>
      <dt className="text-neutral-500">Last sync cost</dt><dd className="text-neutral-200">{r.calls != null ? `${r.calls} call(s), ${num(r.written)} claim(s) updated` : "—"}</dd>
      <dt className="text-neutral-500">Last full pass</dt><dd className="text-neutral-200">{s.last_full_at ? fmtDate(s.last_full_at) : "—"}</dd>
      {s.checkpoint && <><dt className="text-amber-400">Checkpoint</dt><dd className="text-amber-300">{num(s.checkpoint.fetched)} claims saved, resumes from page {s.checkpoint.pages + 1}</dd></>}
    </dl>
  );
}

export function MondayUsageCard() {
  const [u, setU] = useState(null);
  const load = useCallback(() => adminApi.get("/admin/monday-usage").then(({ data }) => setU(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 60000);
  const toggle = async (enabled) => {
    try { await adminApi.put("/admin/monday-usage/background", { enabled }); setU({ ...u, background_sync: enabled }); toast.success(enabled ? "Background sync switched on for this copy" : "Background sync switched off for this copy"); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  if (!u) return <Card title="Monday.com usage" icon={Gauge}><div className="h-32 animate-pulse" /></Card>;
  return (
    <Card title="Monday.com usage" icon={Gauge} testId="wi-usage-card">
      <div className="space-y-4">
        {u.limit_message && <p className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="wi-usage-limit">{u.limit_message}</p>}
        <div><div className="font-heading text-3xl font-bold text-white tabular-nums" data-testid="wi-usage-today">{num(u.today.total)}</div>
          <div className="text-xs text-neutral-500">calls today from this copy of the app (Monday's day resets at 10am Brisbane)</div></div>
        <ul className="text-xs space-y-1" data-testid="wi-usage-breakdown">
          {u.today.by_purpose.length ? u.today.by_purpose.map((p) => (
            <li key={p.label} className="flex justify-between gap-3"><span className="text-neutral-400">{p.label}</span><span className="tabular-nums text-neutral-200">{num(p.calls)}</span></li>
          )) : <li className="text-neutral-600">No Monday calls yet today.</li>}
        </ul>
        <Days days={u.days} />
        <SyncState s={u.sync} />
        <label className="flex items-start gap-3 cursor-pointer border-t border-neutral-800 pt-3">
          <Switch checked={u.background_sync} onCheckedChange={toggle} data-testid="wi-usage-background-switch" />
          <span className="text-xs text-neutral-400"><b className="text-neutral-200">Background sync on this copy</b><br />Off means this copy only reads Monday when someone presses a button. Keep it on for the live app.</span>
        </label>
      </div>
    </Card>
  );
}
